import React, { useEffect } from 'react';

// Composant de tracking des conversions pour les pages d'atterrissage
const ConversionTracker = ({ 
  pageName, 
  metier, 
  onPageView, 
  onCTAClick, 
  onScrollDepth 
}) => {
  useEffect(() => {
    // Tracking de la vue de page
    if (onPageView) {
      onPageView({
        page: pageName,
        metier: metier,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer
      });
    }

    // Tracking du scroll depth
    let maxScroll = 0;
    const trackScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        
        // Déclencher événements aux seuils importants
        if ([25, 50, 75, 90].includes(scrollPercent) && onScrollDepth) {
          onScrollDepth({
            page: pageName,
            metier: metier,
            scrollPercent: scrollPercent,
            timestamp: new Date().toISOString()
          });
        }
      }
    };

    // Tracking du temps passé
    const startTime = Date.now();
    const trackTimeSpent = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      
      // Enregistrer tous les 30 secondes
      if (timeSpent % 30 === 0 && timeSpent > 0) {
        console.log(`[CONVERSION TRACKER] Temps passé: ${timeSpent}s sur ${pageName}`);
      }
    };

    // Event listeners
    window.addEventListener('scroll', trackScroll);
    const timeInterval = setInterval(trackTimeSpent, 1000);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', trackScroll);
      clearInterval(timeInterval);
      
      // Enregistrer le temps final
      const finalTime = Math.round((Date.now() - startTime) / 1000);
      console.log(`[CONVERSION TRACKER] Session terminée: ${finalTime}s sur ${pageName} (${metier})`);
    };
  }, [pageName, metier, onPageView, onScrollDepth]);

  // Fonction helper pour tracker les clics CTA
  const trackCTAClick = (ctaType, ctaText) => {
    const eventData = {
      page: pageName,
      metier: metier,
      ctaType: ctaType, // 'essai_gratuit', 'demo', 'calculer_gains'
      ctaText: ctaText,
      timestamp: new Date().toISOString()
    };

    console.log('[CONVERSION TRACKER] CTA clicked:', eventData);
    
    if (onCTAClick) {
      onCTAClick(eventData);
    }

    // Envoyer vers un endpoint de tracking (à créer)
    try {
      fetch('/api/tracking/cta-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData)
      });
    } catch (error) {
      console.log('[CONVERSION TRACKER] Erreur envoi tracking:', error);
    }
  };

  // Exposer la fonction globalement pour les boutons
  useEffect(() => {
    window.trackCTAClick = trackCTAClick;
    return () => {
      delete window.trackCTAClick;
    };
  }, [trackCTAClick]);

  return null; // Composant invisible
};

// Hook personnalisé pour faciliter l'utilisation
export const useConversionTracker = (pageName, metier) => {
  const trackPageView = (data) => {
    console.log('[CONVERSION TRACKER] Page view:', data);
    
    // Envoyer vers analytics
    try {
      fetch('/api/tracking/page-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.log('[CONVERSION TRACKER] Erreur page view:', error);
    }
  };

  const trackScrollDepth = (data) => {
    console.log('[CONVERSION TRACKER] Scroll depth:', data);
    
    try {
      fetch('/api/tracking/scroll-depth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.log('[CONVERSION TRACKER] Erreur scroll tracking:', error);
    }
  };

  const trackCTAClick = (data) => {
    console.log('[CONVERSION TRACKER] CTA click:', data);
    
    try {
      fetch('/api/tracking/cta-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.log('[CONVERSION TRACKER] Erreur CTA tracking:', error);
    }
  };

  return {
    ConversionTracker: () => (
      <ConversionTracker
        pageName={pageName}
        metier={metier}
        onPageView={trackPageView}
        onScrollDepth={trackScrollDepth}
        onCTAClick={trackCTAClick}
      />
    ),
    trackCTAClick: (ctaType, ctaText) => {
      if (window.trackCTAClick) {
        window.trackCTAClick(ctaType, ctaText);
      }
    }
  };
};

export default ConversionTracker;